/**
 * Discounted incl-VAT ("inkl. moms") line — task 57095 Part C.
 *
 * The `.price-incl-vat` line is server-rendered as base_price × 1.25. For logged-in B2B
 * customers BSS swaps the shown price to a discounted one client-side, so the incl-VAT
 * must follow the DISCOUNTED price, not the base. Rather than hardcode the group discount
 * %s (which would drift from BSS's config), we mirror whatever ex-VAT price is actually
 * shown: incl-VAT = displayed selling price × 1.25. Works for guests (base), any B2B group
 * (discounted), and every variant automatically.
 *
 * Also fixes a UX quirk: on in-page variant switch BSS briefly hides the price container
 * (~1-2s) while it re-swaps. During that window we hide the incl-VAT line too, so the
 * "inkl. moms" text never appears before the price it belongs to.
 */
(() => {
  const VAT_RATE = 1.25;

  /** "1.197,00 kr" / "861,84 kr" -> 1197 / 861.84 (Danish format: . thousands, , decimal). */
  const parsePrice = (text) => {
    const match = (text || '').match(/\d[\d.]*(?:,\d+)?/);
    if (!match) return null;
    const n = parseFloat(match[0].replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };

  const formatMoney = (n) =>
    n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr';

  /** The `[bss-b2b-product-price]` container this incl-VAT line belongs to (its sibling). */
  const findPriceContainer = (inclVatEl) => {
    let el = inclVatEl.previousElementSibling;
    while (el) {
      if (el.matches?.('[bss-b2b-product-price]')) return el;
      el = el.previousElementSibling;
    }
    return inclVatEl.parentElement?.querySelector('[bss-b2b-product-price]') || null;
  };

  /** The current SELLING price shown in the container — skips struck compare-at prices. */
  const readSellingPrice = (container) => {
    let value = null;
    container.querySelectorAll('*').forEach((el) => {
      if (el.children.length) return; // leaf nodes only
      const text = el.textContent.trim();
      if (!/kr/i.test(text) || !/\d/.test(text)) return;
      if (el.offsetParent === null) return; // not rendered
      if (getComputedStyle(el).textDecorationLine.includes('line-through')) return; // struck original
      if (el.closest('.compare-at-price, .visually-hidden')) return;
      const n = parsePrice(text);
      if (n != null) value = n; // keep the LAST valid (BSS renders the discounted price last)
    });
    return value;
  };

  let observer = null;

  const refresh = () => {
    const lines = document.querySelectorAll('.price-incl-vat');
    if (!lines.length) return;
    observer?.disconnect();
    try {
      lines.forEach((line) => {
        // Capture the locale label ("inkl. moms" / "incl. VAT") once, before we overwrite.
        if (!line.dataset.vatLabel) {
          const t = line.textContent;
          const i = t.toLowerCase().lastIndexOf('kr');
          line.dataset.vatLabel = (i >= 0 ? t.slice(i + 2) : t).trim();
        }

        const container = findPriceContainer(line);

        // Mid-swap: BSS hides the container -> hide the incl-VAT so it isn't shown alone.
        if (container && getComputedStyle(container).visibility === 'hidden') {
          line.style.visibility = 'hidden';
          return;
        }

        const price = container ? readSellingPrice(container) : null;
        line.style.visibility = ''; // container is showing -> incl-VAT shows

        // Only rewrite when we could read a price; otherwise keep the server value (no regression).
        if (price != null) {
          const next = `${formatMoney(price * VAT_RATE)} ${line.dataset.vatLabel}`.trim();
          if (line.textContent !== next) line.textContent = next;
        }
      });
    } finally {
      observe();
    }
  };

  let debounceId = null;
  const scheduleRefresh = () => {
    clearTimeout(debounceId);
    debounceId = setTimeout(refresh, 120);
  };

  const observe = () => {
    observer?.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'bss-b2b-product-active'],
      characterData: true,
    });
  };

  const start = () => {
    observer = new MutationObserver(scheduleRefresh);
    refresh();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
